import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  GradientBar,
} from "@sunrun/experience-ui-components";
import { useMemo } from "react";
import { SplatProspectResponse } from "providers/Types";
import { calculateUsageData } from "utils/usageUtils";

const UsageContentSummary = ({
  usage,
  usageData,
  daily,
}: {
  usage: SplatProspectResponse | undefined;
  usageData?: (number | undefined)[];
  daily?: boolean;
}) => {
  const { totalUsage, monthsEntered } = useMemo(
    () => calculateUsageData({ prospect: usage, usageData, daily }),
    [usage, usageData, daily]
  );

  return (
    <Container>
      <Typography
        size={tokens.FONT_SIZE_3}
        color={tokens.BRAND_HERO_BLUE}
        fontWeight={"bold"}
      >
        Usage summary
      </Typography>
      <Content>
        <Column>
          <Typography size={16} color={tokens.BRAND_HERO_BLUE}>
            # of months entered
          </Typography>
          <StyledGradientBar
            midPoint={(monthsEntered / 12) * 100}
            height={8}
            borderRadius={4}
            className="gradient-bar"
          >
            <Typography color={tokens.WHITE} size={16}>
              Months {monthsEntered}/12
            </Typography>
          </StyledGradientBar>
        </Column>
        <Divider />
        <Column>
          <Typography size={16} color={tokens.BRAND_HERO_BLUE}>
            Total usage entered
          </Typography>
          <UsageValue monthsEntered={monthsEntered}>
            {monthsEntered > 0 ? `${totalUsage} kWh` : "Not entered"}
          </UsageValue>
        </Column>
        <Divider />
        <Column>
          <Typography size={16} color={tokens.BRAND_HERO_BLUE}>
            Estimated annual usage
          </Typography>
          <UsageValue monthsEntered={monthsEntered}>
            {monthsEntered > 0
              ? monthsEntered === 12
                ? `${totalUsage} kWh`
                : "See in IHD"
              : "Not entered"}
          </UsageValue>
        </Column>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: 4px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UsageValue = styled.span<{ monthsEntered: number }>`
  font-weight: 600;
  font-size: 16px !important;
  color: ${({ monthsEntered }) =>
    monthsEntered > 0 ? tokens.NIGHT_BLACK_BLUE : tokens.WARNING_40};
`;

const Divider = styled.div`
  width: 2px;
  background-color: ${tokens.HEROBLUE_80};
  max-height: 100%;
  height: 56px;
  margin: auto;
  border-radius: 2px;
`;

const StyledGradientBar = styled(GradientBar)`
  &.gradient-bar {
    padding: 8px 24px;
    min-width: 108px;
  }
`;

export { UsageContentSummary };
